import React, { useState, useEffect, useRef, useCallback } from 'react';
import { City, State } from 'country-state-city';
import { CountrySelect, StateSelect, GetState } from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";
import { formatZipInput, validateZipDigits } from "../utils/validationHelpers";

// ---------------------------------------------------------------------------
// All US cities — computed ONCE at module load from the bundled JS package.
// Zero network requests. Pure in-memory filtering on every keystroke.
// ---------------------------------------------------------------------------
const ALL_US_CITIES = City.getCitiesOfCountry('US').map((c) => ({
  name: c.name,
  stateCode: c.stateCode,
}));

// stateCode → full state name (e.g. "NY" → "New York")
const US_STATE_BY_CODE = {};
State.getStatesOfCountry('US').forEach((s) => {
  US_STATE_BY_CODE[s.isoCode] = s.name;
});

// ---------------------------------------------------------------------------
// State ID cache — needed only to drive react-country-state-city's StateSelect
// defaultValue (which expects a numeric id). One HTTP request, cached forever.
// ---------------------------------------------------------------------------
let cachedStates = null;
let statesPromise = null;

function getStates() {
  if (cachedStates) return Promise.resolve(cachedStates);
  if (statesPromise) return statesPromise;
  statesPromise = GetState(233).then((s) => {
    cachedStates = s;
    return s;
  });
  return statesPromise;
}

function resolveStateId(stateCodeOrName) {
  if (!cachedStates || !stateCodeOrName) return 0;
  const q = stateCodeOrName.toLowerCase();
  const found = cachedStates.find(
    (s) => s.state_code.toLowerCase() === q || s.name.toLowerCase() === q,
  );
  return found ? found.id : 0;
}

// ---------------------------------------------------------------------------
// City search autocomplete — instant, in-memory
// ---------------------------------------------------------------------------
const USCitySearch = ({
  filterStateCode, // optional: restrict results to this state
  defaultValue,
  onSelect,
  onChange,
  disabled,
  placeholder,
}) => {
  const [query, setQuery] = useState(defaultValue || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef(null);
  const queryRef = useRef(query);

  // No useEffect for loading — ALL_US_CITIES is already in memory!

  const runSearch = useCallback(
    (term) => {
      if (!term || term.length < 2) {
        setSuggestions([]);
        setIsOpen(false);
        return;
      }
      const lower = term.toLowerCase();
      // Narrow pool to selected state for more relevant ordering.
      const pool = filterStateCode
        ? ALL_US_CITIES.filter((c) => c.stateCode === filterStateCode)
        : ALL_US_CITIES;

      const starts = [];
      const contains = [];
      for (const c of pool) {
        const n = c.name.toLowerCase();
        if (n.startsWith(lower)) {
          starts.push(c);
          if (starts.length >= 10) break;
        } else if (n.includes(lower)) {
          contains.push(c);
        }
      }
      const result = [...starts, ...contains].slice(0, 8);
      setSuggestions(result);
      setIsOpen(result.length > 0);
    },
    [filterStateCode],
  );

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    queryRef.current = val;
    if (onChange) onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(val), 120);
  };

  const handleFocus = () => {
    if (queryRef.current.length >= 2) runSearch(queryRef.current);
  };

  const handleSelect = (city) => {
    setQuery(city.name);
    setSuggestions([]);
    setIsOpen(false);
    onSelect({
      name: city.name,
      stateCode: city.stateCode,
      stateName: US_STATE_BY_CODE[city.stateCode] || city.stateCode,
    });
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        placeholder={placeholder || 'Search city…'}
        disabled={disabled}
        className="w-full px-3 py-[7px] border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
      />
      {isOpen && (
        <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
          {suggestions.map((city, i) => (
            <li key={`${city.name}-${city.stateCode}-${i}`}>
              <button
                type="button"
                onMouseDown={() => handleSelect(city)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 hover:text-emerald-800 flex justify-between items-center"
              >
                <span>{city.name}</span>
                <span className="text-gray-400 text-xs ml-2 shrink-0">
                  {city.stateCode}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared plain-text input (non-US countries)
// ---------------------------------------------------------------------------
const TextInput = ({ value, onChange, placeholder, disabled }) => (
  <input
    type="text"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    disabled={disabled}
    className="w-full px-3 py-[7px] border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
  />
);

// ---------------------------------------------------------------------------
// Main StateCitySelect
// ---------------------------------------------------------------------------
const StateCitySelect = ({
  countryValue = 'USA',
  stateValue = '',
  cityValue = '',
  onCountryChange,
  onStateChange,
  onCityChange,
  isEditing = false,
  disabled = false,
  countryLabel = 'Country',
  stateLabel = 'State',
  cityLabel = 'City',
  layout = 'grid',
  zipValue = '',
  onZipChange,
  zipLabel = 'ZIP Code',
  /** When true: digits only, max 5 (US), same as DataField ZIP behavior. */
  zipRestrictUsFive = false,
  onZipValidationError,
  zipError = '',
  required = false,
}) => {
  const getCountryId = (val) => {
    // Treat empty/undefined as USA so all forms preselect USA by default.
    if (!val) return 233;
    if (val === 'USA' || val === 'United States' || val === 233) return 233;
    return val.id || 0;
  };

  const [internalCountryId, setInternalCountryId] = useState(getCountryId(countryValue));
  // Numeric ID needed only for StateSelect's defaultValue.
  const [internalStateId, setInternalStateId] = useState(0);
  // String state code used for filtering the city search.
  const [internalStateCode, setInternalStateCode] = useState('');
  // Key bumped to reset city input when state/country changes.
  const [cityInputKey, setCityInputKey] = useState(0);

  const [resolvedCountryName, setResolvedCountryName] = useState(
    !countryValue || countryValue === 'USA' || countryValue === 233
      ? 'United States'
      : typeof countryValue === 'string'
      ? countryValue
      : '',
  );
  const [resolvedStateName, setResolvedStateName] = useState('');
  const [resolvedCityName, setResolvedCityName] = useState('');
  const [isResolving, setIsResolving] = useState(true);

  const resolveStateRef = useRef(stateValue);
  const resolveCityRef = useRef(cityValue);

  // Sync country ID when prop changes.
  useEffect(() => {
    const newId = getCountryId(countryValue);
    setInternalCountryId(newId);
    if (newId === 233) setResolvedCountryName('United States');
    else if (typeof countryValue === 'string') setResolvedCountryName(countryValue);
  }, [countryValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve stored string values → IDs on mount / country change.
  // For US: uses bundled data (US_STATE_BY_CODE) for the name lookup,
  // and one cached GetState() call for the numeric ID the StateSelect widget needs.
  useEffect(() => {
    let active = true;

    const resolve = async () => {
      const sv = resolveStateRef.current;
      const cv = resolveCityRef.current;

      if (!sv && !cv) { setIsResolving(false); return; }

      if (internalCountryId !== 233) {
        setResolvedStateName(sv);
        setResolvedCityName(cv);
        setIsResolving(false);
        return;
      }

      try {
        // One cached HTTP request for numeric IDs needed by StateSelect widget.
        await getStates();
        if (!active) return;

        if (sv) {
          // Resolve state name from bundled data (instant).
          const q = sv.toLowerCase();
          const stateCode = Object.keys(US_STATE_BY_CODE).find(
            (code) => code.toLowerCase() === q,
          );
          const stateName = stateCode
            ? US_STATE_BY_CODE[stateCode]
            : (US_STATE_BY_CODE[sv.toUpperCase()] || sv);
          const resolvedCode = stateCode || sv.toUpperCase();

          setInternalStateCode(resolvedCode);
          setResolvedStateName(stateName);

          // Numeric ID from cached GetState result.
          const numericId = resolveStateId(sv);
          setInternalStateId(numericId);
        }

        if (cv) setResolvedCityName(cv);
      } catch (e) {
        console.error('Error resolving location IDs:', e);
      } finally {
        if (active) setIsResolving(false);
      }
    };

    setIsResolving(true);
    resolve();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalCountryId]);

  // ---- Handlers ----

  const handleCountrySelect = (e) => {
    resolveStateRef.current = '';
    resolveCityRef.current = '';
    setInternalCountryId(e.id);
    setResolvedCountryName(e.name);
    setInternalStateId(0);
    setInternalStateCode('');
    setResolvedStateName('');
    setResolvedCityName('');
    setCityInputKey((k) => k + 1);
    if (onCountryChange) onCountryChange(e.id === 233 ? 'USA' : e.name);
    if (onStateChange) onStateChange('');
    if (onCityChange) onCityChange('');
  };

  const handleStateSelect = (e) => {
    setInternalStateId(e.id);
    setInternalStateCode(e.state_code || '');
    setResolvedStateName(e.name);
    setResolvedCityName('');
    setCityInputKey((k) => k + 1);
    onStateChange(e.state_code || e.name);
    onCityChange('');
  };

  // City selected → auto-populate state + country instantly (bundled data).
  const handleUSCitySelect = (city) => {
    // city = { name, stateCode, stateName }
    setResolvedCityName(city.name);
    setInternalStateCode(city.stateCode);
    setResolvedStateName(city.stateName);

    // Resolve the numeric state ID so StateSelect shows the right value.
    getStates().then(() => {
      const numId = resolveStateId(city.stateCode);
      setInternalStateId(numId);
    });

    if (internalCountryId !== 233) {
      setInternalCountryId(233);
      setResolvedCountryName('United States');
      if (onCountryChange) onCountryChange('USA');
    }
    onCityChange(city.name);
    onStateChange(city.stateCode);
  };

  // ---- View mode ----
  if (!isEditing) {
    return (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{countryLabel}</label>
          <p className="text-sm text-gray-900">{resolvedCountryName || countryValue || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{stateLabel}</label>
          <p className="text-sm text-gray-900">{stateValue || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{cityLabel}</label>
          <p className="text-sm text-gray-900">{cityValue || 'N/A'}</p>
        </div>
        {onZipChange !== undefined && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{zipLabel}</label>
            <p className="text-sm text-gray-900">{zipValue || 'N/A'}</p>
          </div>
        )}
      </div>
    );
  }

  if (isResolving) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse bg-gray-100 h-10 w-full rounded-md" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="animate-pulse bg-gray-100 h-10 w-full rounded-md" />
          <div className="animate-pulse bg-gray-100 h-10 w-full rounded-md" />
        </div>
      </div>
    );
  }

  const isUSA = internalCountryId === 233;

  // ---- Edit mode ----
  return (
    <div className="space-y-4">

      {/* ── City ── */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">{cityLabel} {required && <span className="text-red-500">*</span>}</label>
        {isUSA ? (
          <USCitySearch
            key={cityInputKey}
            filterStateCode={internalStateCode || undefined}
            defaultValue={resolvedCityName || cityValue}
            onSelect={handleUSCitySelect}
            onChange={onCityChange}
            disabled={disabled}
            placeholder={`Search ${cityLabel}`}
          />
        ) : (
          <TextInput
            value={cityValue}
            onChange={onCityChange}
            placeholder={`Enter ${cityLabel.toLowerCase()}`}
            disabled={disabled}
          />
        )}
      </div>

      {/* ── State ── */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">{stateLabel} {required && <span className="text-red-500">*</span>}</label>
        {isUSA ? (
          <StateSelect
            countryid={233}
            onChange={handleStateSelect}
            placeHolder={`Select ${stateLabel}`}
            defaultValue={internalStateId ? { id: internalStateId, name: resolvedStateName } : null}
            disabled={disabled}
          />
        ) : (
          <TextInput
            value={stateValue}
            onChange={onStateChange}
            placeholder={`Enter ${stateLabel.toLowerCase()}`}
            disabled={disabled}
          />
        )}
      </div>

      {/* ── Country ── */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">{countryLabel} {required && <span className="text-red-500">*</span>}</label>
        <CountrySelect
          onChange={handleCountrySelect}
          placeHolder={`Select ${countryLabel}`}
          defaultValue={internalCountryId ? { id: internalCountryId, name: resolvedCountryName } : null}
          disabled={disabled}
        />
      </div>

      {/* ── ZIP ── */}
      {onZipChange !== undefined && (
        <div className="relative">
          <div className="flex justify-between items-center mb-0.5">
            <label className="block text-sm font-medium text-gray-700">{zipLabel}</label>
            {zipRestrictUsFive && zipError ? (
              <span className="text-[10px] text-red-500 font-bold bg-red-50 px-1.5 py-0.5 rounded border border-red-100 uppercase tracking-tight">
                Invalid
              </span>
            ) : null}
          </div>
          {zipRestrictUsFive ? (
            <input
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              value={formatZipInput(String(zipValue ?? ""))}
              onChange={(e) => {
                const next = formatZipInput(e.target.value);
                onZipChange(next);
                if (onZipValidationError) {
                  if (!next) onZipValidationError(null);
                  else if (validateZipDigits(next)) onZipValidationError(null);
                }
              }}
              onBlur={(e) => {
                if (!onZipValidationError) return;
                const digits = e.target.value.replace(/\D/g, "");
                if (digits.length > 0 && digits.length !== 5) {
                  onZipValidationError("Invalid Zip");
                } else {
                  onZipValidationError(null);
                }
              }}
              maxLength={5}
              placeholder="Enter ZIP code"
              disabled={disabled}
              className={`w-full px-3 py-[7px] border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                zipError ? "border-red-500 bg-red-50/30" : "border-gray-300"
              }`}
            />
          ) : (
            <TextInput
              value={zipValue}
              onChange={onZipChange}
              placeholder="Enter ZIP code"
              disabled={disabled}
            />
          )}
        </div>
      )}

    </div>
  );
};

export default StateCitySelect;
